import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { addEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, pipe, switchMap } from 'rxjs';

import { FileExtensionGroup, Specification, SpecificationParameter } from '@celum/formats/domain';

import { FORMAT_SPECIFICATION_SERVICE, FORMAT_STORE } from '../../model';
import { NotificationService } from '../../services/notification.service';
import {
  extractGroups,
  extractSpecFileExtensions,
  extractSpecParameters,
  getSpecKey,
  mergeProcessRefsInContract,
  resolveExternalRefs
} from '../../util/specification-contract.util';

type SpecificationState = {
  isSpecsLoading: boolean;
  isSpecContractLoading: boolean;
  selectedSpecId: number;
  specParameters: SpecificationParameter[];
  fileExtensionsGroups: FileExtensionGroup[];
  specDescription: string;
  groups?: string[];
};

const initialState: SpecificationState = {
  isSpecsLoading: true,
  isSpecContractLoading: true,
  selectedSpecId: null,
  specParameters: null,
  fileExtensionsGroups: [],
  specDescription: '',
  groups: null
};

/**
 * Single implementation of specification-related state, shared by the tenant-scoped format view and the
 * base-formats admin view - there is only one `Specification` entity, only the backing service (tenant-scoped
 * `SpecificationsService` vs global `SpecificationDefinitionsService`, bound to `FORMAT_SPECIFICATION_SERVICE`) and the
 * format store used for default-spec-selection (`FormatStore` vs `BaseFormatStore`, bound to `FORMAT_STORE`) differ per context.
 */
export const SpecificationStore = signalStore(
  withEntities<Specification>(),
  withState(initialState),
  withComputed(({ selectedSpecId, entityMap, fileExtensionsGroups }) => ({
    selectedSpec: computed(() => entityMap()[selectedSpecId()]),
    fileExtensions: computed(() => fileExtensionsGroups().reduce((acc, group) => [...acc, ...group.extensions], []))
  })),
  withMethods(
    (store, specService = inject(FORMAT_SPECIFICATION_SERVICE), notificationService = inject(NotificationService), formatStore = inject(FORMAT_STORE)) => {
      function loadSpecs(): void {
        patchState(store, { isSpecsLoading: true });

        specService.getSpecifications().subscribe({
          next: ({ content }) => {
            patchState(store, addEntities(content));

            if (store.selectedSpecId() === null && !formatStore.selectedFormat()) {
              // if there is no selected specification, and it is not edit mode, populate the download image as a default spec, or the first available if it does not exist
              const downloadImageSpec = content.find(spec => spec.key === 'downloadImage');
              updateSelectedSpecId(downloadImageSpec?.id || content[0]?.id);
            }

            patchState(store, { isSpecsLoading: false });
          },
          error: error => {
            console.error('SpecificationStore: error on loading specifications', error.error);

            notificationService.error('FORMAT_LIST.SNACKBAR.LOAD_SPEC_ERROR');
            patchState(store, { isSpecsLoading: false });
          }
        });
      }

      function updateSelectedSpecId(id: number): void {
        if (store.selectedSpecId() === id) {
          return;
        }

        patchState(store, { selectedSpecId: id, specParameters: null });
      }

      const loadSpecContract = rxMethod<number>(
        pipe(
          switchMap(specId => {
            if (!specId && specId !== 0) {
              return EMPTY;
            }

            const { key } = getSpecKey(store.entityMap()?.[specId], formatStore.selectedFormat()?.specificationReference);

            patchState(store, { isSpecContractLoading: true, fileExtensionsGroups: [] });

            return specService.getSpecificationContract(key).pipe(
              switchMap(contract => mergeProcessRefsInContract(contract, specService)),
              switchMap(contract => resolveExternalRefs(contract, specService)),
              tapResponse({
                next: contract => {
                  const specParameters = extractSpecParameters(contract);
                  patchState(store, {
                    specParameters,
                    fileExtensionsGroups: extractSpecFileExtensions(contract),
                    specDescription: contract.info.description,
                    groups: extractGroups(specParameters)
                  });
                },
                error: error => {
                  console.error('SpecificationStore: error on loading specification contract', error);
                  notificationService.error('FORMAT_LIST.SNACKBAR.LOAD_PARAMS_ERROR');
                },
                finalize: () => patchState(store, { isSpecContractLoading: false })
              })
            );
          })
        )
      );

      return {
        loadSpecs,
        loadSpecContract,
        updateSelectedSpecId
      };
    }
  ),
  withHooks({
    onInit(store) {
      store.loadSpecs();
    }
  })
);
